import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Skeleton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Divider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import TimelineIcon from '@mui/icons-material/Timeline';
import EggIcon from '@mui/icons-material/Egg';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useProductionCurve } from '../hooks/useEggs';

const BATCH_COLORS = ['#ff9800', '#42a5f5', '#66bb6a', '#ab47bc', '#ef5350', '#26c6da'];
const STANDARD_COLOR = 'rgba(255,255,255,0.25)';
const AXIS_STYLE = { fill: '#9e9e9e', fontSize: 11 };
const GRID_STYLE = { stroke: 'rgba(255,255,255,0.06)' };

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  sub?: string;
  color: string;
  badge?: { label: string; color: string };
}

function StatCard({ icon, label, value, sub, color, badge }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderTop: `3px solid ${color}`,
        background: `linear-gradient(135deg, ${color}12 0%, transparent 60%)`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color }}>
          {icon}
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}
          >
            {label}
          </Typography>
        </Box>
        {badge && (
          <Chip
            label={badge.label}
            size="small"
            sx={{ bgcolor: `${badge.color}22`, color: badge.color, fontSize: 10, height: 20 }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
        {value ?? '—'}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, minWidth: 180 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', mb: 1, fontWeight: 600 }}
      >
        Week {label}
      </Typography>
      {payload.map((p: any) => (
        <Box
          key={p.dataKey}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, mb: 0.25 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: p.name === 'Standard' ? 1 : '50%',
                bgcolor: p.color,
              }}
            />
            <Typography variant="caption">{p.name}</Typography>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {p.value != null ? `${Number(p.value).toFixed(1)}%` : '—'}
          </Typography>
        </Box>
      ))}
      {payload.length >= 2 && payload[0]?.value != null && payload[1]?.value != null && (
        <>
          <Divider sx={{ my: 0.75, opacity: 0.3 }} />
          {(() => {
            const actual = payload.find((p: any) => p.name !== 'Standard')?.value;
            const std = payload.find((p: any) => p.name === 'Standard')?.value;
            if (actual == null || std == null) return null;
            const diff = actual - std;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {diff >= 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 12, color: '#66bb6a' }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 12, color: '#f44336' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{ color: diff >= 0 ? '#66bb6a' : '#f44336', fontWeight: 600 }}
                >
                  {diff >= 0 ? '+' : ''}
                  {diff.toFixed(1)}% vs standard
                </Typography>
              </Box>
            );
          })()}
        </>
      )}
    </Paper>
  );
};

function performanceLabel(deviation: number | null): { label: string; color: string } {
  if (deviation == null) return { label: 'No data', color: '#9e9e9e' };
  if (deviation >= 2) return { label: 'Above standard', color: '#66bb6a' };
  if (deviation >= -3) return { label: 'On track', color: '#ff9800' };
  if (deviation >= -8) return { label: 'Slightly below', color: '#ffa726' };
  return { label: 'Below standard', color: '#f44336' };
}

export default function ProductionCurve() {
  const [selectedProcurement, setSelectedProcurement] = useState<number | ''>('');

  const { data, isLoading, error } = useProductionCurve({
    procurement_id: selectedProcurement || undefined,
  });

  const procurements: any[] = useMemo(() => data?.procurements ?? [], [data]);

  // All active procurements for the selector (unfiltered)
  const { data: allData } = useProductionCurve();
  const allProcurements: any[] = useMemo(() => allData?.procurements ?? [], [allData]);

  // Which procurement(s) to display
  const displayed = useMemo(() => {
    if (selectedProcurement)
      return procurements.filter((p) => p.procurement_id === selectedProcurement);
    return procurements;
  }, [procurements, selectedProcurement]);

  // Build chart data aligned on week number
  const chartData = useMemo(() => {
    if (!displayed.length) return [];
    const weekMap = new Map<number, Record<string, any>>();

    for (const proc of displayed) {
      for (const w of proc.weekly ?? []) {
        if (!weekMap.has(w.week)) weekMap.set(w.week, { week: w.week, standard: w.standard });
        const entry = weekMap.get(w.week);
        if (entry) entry[`actual_${proc.procurement_id}`] = w.lay_rate;
      }
    }

    return Array.from(weekMap.values()).sort((a, b) => a.week - b.week);
  }, [displayed]);

  // Summary for cards — use first (most relevant) procurement when one is selected, else combined
  const primary = displayed[0];
  const summary = primary?.summary;

  const perfBadge = performanceLabel(summary?.deviation ?? null);

  const fmtPct = (v: number | null | undefined) => (v != null ? `${v.toFixed(1)}%` : null);
  const fmtDev = (v: number | null | undefined) => {
    if (v == null) return null;
    return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load production curve data.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <TimelineIcon sx={{ color: '#42a5f5', fontSize: 28 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(90deg, #42a5f5, #66bb6a)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Production Curve
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Actual weekly lay rate vs {data?.breed_standard_name ?? 'breed standard'} — by week of
            production
          </Typography>
        </Box>

        {allProcurements.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Batch</InputLabel>
            <Select
              label="Batch"
              value={selectedProcurement}
              onChange={(e) => setSelectedProcurement(e.target.value as number | '')}
            >
              <MenuItem value="">All active batches</MenuItem>
              {allProcurements.map((p) => (
                <MenuItem key={p.procurement_id} value={p.procurement_id}>
                  {p.identifier}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2}>
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
              </Grid>
            ))
          : [
              {
                icon: <EggIcon sx={{ fontSize: 20 }} />,
                label: 'Current Lay Rate',
                value: fmtPct(summary?.current_lay_rate),
                sub: summary?.current_week
                  ? `Week ${summary.current_week} of production`
                  : undefined,
                color: '#42a5f5',
              },
              {
                icon:
                  summary?.deviation != null && summary.deviation >= 0 ? (
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 20 }} />
                  ),
                label: 'vs Breed Standard',
                value: fmtDev(summary?.deviation),
                sub: `Avg ${fmtPct(summary?.avg_lay_rate)} · Standard ${fmtPct(summary?.avg_standard)}`,
                color: summary?.deviation != null && summary.deviation >= 0 ? '#66bb6a' : '#f44336',
                badge: perfBadge,
              },
              {
                icon: <StarIcon sx={{ fontSize: 20 }} />,
                label: 'Peak Lay Rate',
                value: fmtPct(summary?.peak_lay_rate),
                sub: summary?.peak_week ? `Reached at week ${summary.peak_week}` : undefined,
                color: '#ff9800',
              },
              {
                icon: <CalendarTodayIcon sx={{ fontSize: 20 }} />,
                label: 'Weeks on Lay',
                value: primary?.total_weeks != null ? `${primary.total_weeks} wks` : null,
                sub: primary?.first_lay_date ? `Since ${primary.first_lay_date}` : undefined,
                color: '#ab47bc',
              },
            ].map((card) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                <StatCard {...card} />
              </Grid>
            ))}
      </Grid>

      {/* Chart */}
      {isLoading ? (
        <Skeleton variant="rectangular" height={380} sx={{ borderRadius: 2 }} />
      ) : chartData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No production data found for this selection.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Lay Rate by Week of Production
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                { color: '#66bb6a', label: '90% — Excellent' },
                { color: '#ff9800', label: '80% — Good' },
                { color: '#f44336', label: '70% — Acceptable' },
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 20, height: 2, bgcolor: color, opacity: 0.6 }} />
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            X-axis: weeks since first egg · Dashed line: {data?.breed_standard_name}
          </Typography>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis
                dataKey="week"
                tick={AXIS_STYLE}
                label={{
                  value: 'Week of Production',
                  position: 'insideBottom',
                  offset: -2,
                  fill: '#9e9e9e',
                  fontSize: 11,
                }}
                height={40}
              />
              <YAxis tick={AXIS_STYLE} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />

              {/* Reference lines */}
              <ReferenceLine y={90} stroke="#66bb6a" strokeOpacity={0.4} strokeDasharray="4 4" />
              <ReferenceLine y={80} stroke="#ff9800" strokeOpacity={0.4} strokeDasharray="4 4" />
              <ReferenceLine y={70} stroke="#f44336" strokeOpacity={0.4} strokeDasharray="4 4" />

              {/* Breed standard */}
              <Line
                type="monotone"
                dataKey="standard"
                name="Standard"
                stroke={STANDARD_COLOR}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls
              />

              {/* Actual lines — one per batch */}
              {displayed.map((proc, i) => (
                <Line
                  key={proc.procurement_id}
                  type="monotone"
                  dataKey={`actual_${proc.procurement_id}`}
                  name={proc.identifier}
                  stroke={BATCH_COLORS[i % BATCH_COLORS.length]}
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Per-batch summary table when showing multiple */}
      {!isLoading && displayed.length > 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Batch Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {displayed.map((proc, i) => {
              const perf = performanceLabel(proc.summary?.deviation ?? null);
              return (
                <Box
                  key={proc.procurement_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${BATCH_COLORS[i % BATCH_COLORS.length]}33`,
                    bgcolor: `${BATCH_COLORS[i % BATCH_COLORS.length]}08`,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: BATCH_COLORS[i % BATCH_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {proc.identifier}
                    </Typography>
                  </Box>
                  {[
                    { label: 'Current', value: fmtPct(proc.summary?.current_lay_rate) },
                    { label: 'Avg', value: fmtPct(proc.summary?.avg_lay_rate) },
                    { label: 'Peak', value: fmtPct(proc.summary?.peak_lay_rate) },
                    { label: 'vs Std', value: fmtDev(proc.summary?.deviation) },
                    {
                      label: 'Weeks',
                      value: proc.total_weeks != null ? `${proc.total_weeks}w` : null,
                    },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ textAlign: 'center', minWidth: 60 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', display: 'block' }}
                      >
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {value ?? '—'}
                      </Typography>
                    </Box>
                  ))}
                  <Chip
                    label={perf.label}
                    size="small"
                    sx={{ bgcolor: `${perf.color}22`, color: perf.color, fontSize: 11, ml: 'auto' }}
                  />
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
